<script setup lang="ts">
    import { ref, computed, toRef } from 'vue'
    import { MonthPicker } from 'vue-month-picker'

    import type { MonthYear } from '@common/types'
    import { MonthYearHelpers } from '@common/types'
    import type { MonthPickerDate, MonthPickerDialogViewModel } from '@common/Components/MonthPickerDialog'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: MonthPickerDialogViewModel
    }>()

    const emit = defineEmits<{
        (e: 'update-date', value: MonthYear | null): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- local state ----------------

    const date = ref<MonthPickerDate | null>(null)

    // ---------------- derived ----------------

    const opened = computed(() => vm.value?.isOpened ?? false)
    const title = computed(() => vm.value.title)
    const description = computed(() => vm.value.description)

    const defaultYear = computed(() => vm.value.defaultDate?.Year ?? null)
    const defaultMonth = computed(() => vm.value.defaultDate?.Month ?? null)

    const canConfirm = computed(() =>
        vm.value.valueRequired === true
            ? date.value != null
            : true
    )

    // ---------------- handlers ----------------

    function onDateChange(d: MonthPickerDate)
    {
        date.value = d
    }

    function onDateClear()
    {
        date.value = null
    }

    function confirm()
    {
        const result =
            date.value == null
                ? null
                : MonthYearHelpers.build(date.value.year, date.value.monthIndex)

        emit('update-date', result)
        close()
    }

    function close()
    {
        vm.value.close()
        date.value = null
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{title}}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div v-if="description" class="alert alert-warning" role="alert">
                            {{description}}
                        </div>
                        <month-picker @clear="onDateClear" @change="onDateChange"
                                      :clearable="true"
                                      :no-default="vm.defaultDate == null"
                                      :default-month="defaultMonth"
                                      :default-year="defaultYear" />
                    </div>

                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                        <button @click="confirm" :disabled="canConfirm === false" type="button" class="btn btn-primary">Confirm</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<style scoped>
    .month-picker__container {
        width: 100%;
    }
</style>